import { scopeQueryKey } from '../data/cacheNamespace';
import type { QueryFilters } from '@tanstack/react-query';

const baseQueryKeys = {
  dataGrid: (path: string, ...parts: Array<string | number | null | undefined>) =>
    ['datagrid', path, ...parts.map((part) => part ?? 'all')] as const,
  projects: (orgId?: string | null) => ['projects', orgId ?? 'personal'] as const,
  projectDetail: (slug: string | null, orgId?: string | null) =>
    ['projects', 'detail', orgId ?? 'personal', slug] as const,
  projectById: (id: string | null, orgId?: string | null) =>
    ['projects', 'detail-by-id', orgId ?? 'personal', id ?? 'unknown'] as const,
  projectCsvMapping: (projectId: string | null, orgId?: string | null) =>
    ['projects', 'csv-mapping', orgId ?? 'personal', projectId ?? 'unknown'] as const,
  projectSummaries: (orgId?: string | null, projectIdsKey?: string | null) =>
    ['projects', 'summary', orgId ?? 'personal', projectIdsKey ?? 'all'] as const,
  features: (projectId?: string | null, orgId?: string | null, deliveryKey?: string | null) =>
    ['features', orgId ?? 'personal', projectId ?? 'all', deliveryKey ?? 'all'] as const,
  featureSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['features', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  featureDetail: (
    slug: string | null,
    orgId?: string | null,
    projectId?: string | null,
    deliveryKey?: string | null,
  ) =>
    [
      'features',
      'detail',
      orgId ?? 'personal',
      projectId ?? 'all',
      slug,
      deliveryKey ?? 'all',
    ] as const,
  releases: (projectId?: string | null, orgId?: string | null, deliveryKey?: string | null) =>
    ['releases', orgId ?? 'personal', projectId ?? 'all', deliveryKey ?? 'all'] as const,
  releaseSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['releases', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  releaseDetail: (
    slug: string | null,
    orgId?: string | null,
    projectId?: string | null,
    deliveryKey?: string | null,
  ) =>
    [
      'releases',
      'detail',
      orgId ?? 'personal',
      projectId ?? 'all',
      slug,
      deliveryKey ?? 'all',
    ] as const,
  suites: (projectId?: string | null, orgId?: string | null) =>
    ['suites', orgId ?? 'personal', projectId ?? 'all'] as const,
  suiteSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['suites', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  suiteSummaryPrefix: () => ['suites', 'summary'] as const,
  suiteDetail: (slug: string | null, orgId?: string | null, projectId?: string | null) =>
    ['suites', 'detail', orgId ?? 'personal', projectId ?? 'all', slug] as const,
  suiteRuns: (projectId?: string | null, orgId?: string | null) =>
    ['suiteRuns', orgId ?? 'personal', projectId ?? 'all'] as const,
  suiteRunSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['suiteRuns', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  suiteRunSummaryPrefix: () => ['suiteRuns', 'summary'] as const,
  executionRunSummary: (
    projectId?: string | null,
    orgId?: string | null,
    filtersKey?: string | null,
  ) =>
    [
      'executionRuns',
      'summary',
      orgId ?? 'personal',
      projectId ?? 'all',
      filtersKey ?? 'all',
    ] as const,
  executionRunSummaryPrefix: () => ['executionRuns', 'summary'] as const,
  suiteRunDetail: (id: string | null, orgId?: string | null) =>
    ['suiteRuns', 'detail', orgId ?? 'personal', id] as const,
  tests: (projectId?: string | null, orgId?: string | null) =>
    ['tests', orgId ?? 'personal', projectId ?? 'all'] as const,
  testSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['tests', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  testSummaryPrefix: () => ['tests', 'summary'] as const,
  testDetail: (slug: string | null, orgId?: string | null, projectId?: string | null) =>
    ['tests', 'detail', orgId ?? 'personal', projectId ?? 'all', slug] as const,
  builds: (projectId?: string | null, orgId?: string | null) =>
    ['builds', orgId ?? 'personal', projectId ?? 'all'] as const,
  jobs: (projectId?: string | null, orgId?: string | null) =>
    ['jobs', orgId ?? 'personal', projectId ?? 'all'] as const,
  buildSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['builds', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  buildDetail: (id: string | null, orgId?: string | null) =>
    ['builds', 'detail', orgId ?? 'personal', id] as const,
  jobDetail: (id: string | null, orgId?: string | null) =>
    ['jobs', 'detail', orgId ?? 'personal', id] as const,
  buildRunDetail: (pipelineId: string | null, runNumber: number | null, orgId?: string | null) =>
    [
      'builds',
      'detail-run',
      orgId ?? 'personal',
      pipelineId ?? 'unknown',
      runNumber ?? 'unknown',
    ] as const,
  pipelines: (projectId?: string | null, orgId?: string | null) =>
    ['pipelines', orgId ?? 'personal', projectId ?? 'all'] as const,
  pipelineSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['pipelines', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  pipelineSummaryPrefix: () => ['pipelines', 'summary'] as const,
  pipelineRunSummary: (
    projectId?: string | null,
    orgId?: string | null,
    filtersKey?: string | null,
  ) =>
    [
      'pipeline-executions',
      'summary',
      orgId ?? 'personal',
      projectId ?? 'all',
      filtersKey ?? 'all',
    ] as const,
  pipelineRunSummaryPrefix: () => ['pipeline-executions', 'summary'] as const,
  pipelineDetail: (id: string | null, orgId?: string | null) =>
    ['pipelines', 'detail', orgId ?? 'personal', id] as const,
  pipelineDetailPrefix: (orgId?: string | null) =>
    ['pipelines', 'detail', orgId ?? 'personal'] as const,
  pipelineStageDetail: (id: string | null, orgId?: string | null) =>
    ['stages', 'detail', orgId ?? 'personal', id] as const,
  pipelineStageExecutionDetail: (id: string | null, orgId?: string | null) =>
    ['stage-runs', 'detail', orgId ?? 'personal', id] as const,
  testRuns: (projectId?: string | null, orgId?: string | null) =>
    ['testRuns', orgId ?? 'personal', projectId ?? 'all'] as const,
  testRunSummary: (projectId?: string | null, orgId?: string | null, filtersKey?: string | null) =>
    ['testRuns', 'summary', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  testRunSummaryPrefix: () => ['testRuns', 'summary'] as const,
  testRunDetail: (id: string | null, orgId?: string | null) =>
    ['testRuns', 'detail', orgId ?? 'personal', id] as const,
  featureTests: (featureId: string | null) => ['featureTests', featureId ?? 'unknown'] as const,
  featureFlags: (search?: string | null) => ['feature-flags', 'list', search ?? 'all'] as const,
  featureFlagsPrefix: () => ['feature-flags'] as const,
  publicFeatureFlags: (keys?: string[] | null) =>
    ['feature-flags', 'public', keys?.length ? keys.join(',') : 'all'] as const,
  featureFlagResolve: (keysKey?: string | null, tokenKey?: string | null) =>
    ['feature-flags', 'resolve', keysKey ?? 'all', tokenKey ?? 'anonymous'] as const,
  metrics: (
    orgId?: string | null,
    projectId?: string | null,
    scope?: string | null,
    range?: string | null,
    statusKey?: string | null,
    nameKey?: string | null,
    entityKey?: string | null,
    day?: string | null,
    rangeKey?: string | null,
    latestOnlyKey?: string | null,
    refreshKey?: string | number | null,
    tagsKey?: string | null,
  ) =>
    [
      'metrics',
      orgId ?? 'personal',
      projectId ?? 'all',
      scope ?? 'all',
      range ?? 'all',
      statusKey ?? 'all',
      nameKey ?? 'all',
      entityKey ?? 'all',
      day ?? 'all',
      rangeKey ?? 'all',
      latestOnlyKey ?? 'all',
      tagsKey ?? 'all',
      refreshKey ?? 'all',
    ] as const,
  metricsBatch: (
    orgId?: string | null,
    scope?: string | null,
    range?: string | null,
    entityKeys?: string | null,
    rangeKey?: string | null,
  ) =>
    [
      'metrics-batch',
      orgId ?? 'personal',
      scope ?? 'all',
      range ?? 'all',
      entityKeys ?? 'all',
      rangeKey ?? 'all',
    ] as const,
  metricsBatchPrefix: () => ['metrics-batch'] as const,
  metricsPrefix: () => ['metrics'] as const,
  users: (orgId?: string | null) => ['users', orgId ?? 'personal'] as const,
  organisationInvites: (orgId?: string | null) =>
    ['organisation-invites', orgId ?? 'personal'] as const,
  superuserUsers: () => ['superuser', 'users'] as const,
  superuserOrganisations: () => ['superuser', 'organisations'] as const,
  superuserAllowedSignups: () => ['superuser', 'allowed-signups'] as const,
  superuserDiagnostics: () => ['superuser', 'diagnostics'] as const,
  superuserErrorEvents: () => ['superuser', 'error-events'] as const,
  superuserFeedback: () => ['superuser', 'feedback'] as const,
  currentUserRole: (userId?: string | null, orgId?: string | null) =>
    ['users', 'role', orgId ?? 'personal', userId ?? 'unknown'] as const,
  currentUserRolePrefix: () => ['users', 'role'] as const,
  userDetail: (id: string | null, orgId?: string | null) =>
    ['users', 'detail', orgId ?? 'personal', id] as const,
  organisations: (userId?: string | null) => ['organisations', userId ?? 'current'] as const,
  organisationsPrefix: () => ['organisations'] as const,
  organisationDetail: (id: string | null) => ['organisations', 'detail', id] as const,
  organisationEntitlements: (id: string | null) =>
    ['organisations', 'entitlements', id ?? 'unknown'] as const,
  releaseDetails: () => ['releases', 'detail'] as const,
  uploadDetail: (id: string | null, orgId?: string | null) =>
    ['uploads', 'detail', orgId ?? 'personal', id] as const,
  uploads: (orgId?: string | null, projectId?: string | null) =>
    ['uploads', orgId ?? 'personal', projectId ?? 'all'] as const,
  coverageUploads: (orgId?: string | null, projectId?: string | null, filtersKey?: string | null) =>
    ['uploads', 'coverage', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  uploadArtifactDetail: (
    uploadId: string | null,
    artifactId: string | null,
    orgId?: string | null,
  ) => ['uploads', 'artifact', orgId ?? 'personal', uploadId, artifactId] as const,
  uploadTokens: (projectId?: string | null, orgId?: string | null) =>
    ['uploads', 'tokens', orgId ?? 'personal', projectId ?? 'all'] as const,
  resultsFiles: (orgId?: string | null, projectId?: string | null) =>
    ['results-files', orgId ?? 'personal', projectId ?? 'all'] as const,
  executionRuns: (orgId?: string | null, projectId?: string | null) =>
    ['execution-runs', orgId ?? 'personal', projectId ?? 'all'] as const,
  coverageRuns: (orgId?: string | null, projectId?: string | null, filtersKey?: string | null) =>
    ['coverage-runs', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  coverageRunSummary: (
    orgId?: string | null,
    projectId?: string | null,
    filtersKey?: string | null,
  ) =>
    [
      'coverage-runs',
      'summary',
      orgId ?? 'personal',
      projectId ?? 'all',
      filtersKey ?? 'all',
    ] as const,
  coverageRunDetail: (id: string | null, orgId?: string | null) =>
    ['coverage-runs', 'detail', orgId ?? 'personal', id ?? 'unknown'] as const,
  coveragePreviousRun: (id: string | null, orgId?: string | null) =>
    ['coverage-runs', 'previous', orgId ?? 'personal', id ?? 'unknown'] as const,
  coverageFiles: (orgId?: string | null, projectId?: string | null, filtersKey?: string | null) =>
    ['coverage-files', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  coverageFileDetail: (id: string | null, orgId?: string | null) =>
    ['coverage-files', 'detail', orgId ?? 'personal', id ?? 'unknown'] as const,
  coverageFilesForRun: (coverageRunId: string | null, orgId?: string | null) =>
    ['coverage-files', 'run', orgId ?? 'personal', coverageRunId ?? 'unknown'] as const,
  codeDirectories: (orgId?: string | null, projectId?: string | null, filtersKey?: string | null) =>
    ['code-directories', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  codeDirectoryDetail: (id: string | null, orgId?: string | null) =>
    ['code-directories', 'detail', orgId ?? 'personal', id ?? 'unknown'] as const,
  codeFiles: (orgId?: string | null, projectId?: string | null, filtersKey?: string | null) =>
    ['code-files', orgId ?? 'personal', projectId ?? 'all', filtersKey ?? 'all'] as const,
  codeFileDetail: (id: string | null, orgId?: string | null) =>
    ['code-files', 'detail', orgId ?? 'personal', id ?? 'unknown'] as const,
  uploadTableTags: (
    tableKey: 'execution-runs' | 'results-files' | 'uploads',
    orgId?: string | null,
    projectId?: string | null,
    filtersKey?: string | null,
  ) =>
    [
      'upload-table-tags',
      tableKey,
      orgId ?? 'personal',
      projectId ?? 'all',
      filtersKey ?? 'all',
    ] as const,
  executionRunDetail: (id: string | null, orgId?: string | null) =>
    ['execution-runs', 'detail', orgId ?? 'personal', id] as const,
  resultsFileDetailPrefix: (orgId?: string | null) =>
    ['results-files', 'detail', orgId ?? 'personal'] as const,
  resultsFileDetail: (slug: string | null, orgId?: string | null) =>
    ['results-files', 'detail', orgId ?? 'personal', slug] as const,
  metadataTerms: (category: string, orgId?: string | null) =>
    ['metadata', orgId ?? 'personal', category] as const,
  tagOptions: (projectId?: string | null, orgId?: string | null) =>
    ['tags', orgId ?? 'personal', projectId ?? 'all'] as const,
  suiteOptions: (projectId?: string | null, orgId?: string | null) =>
    ['suites', 'options', orgId ?? 'personal', projectId ?? 'all'] as const,
};

export const queryKeys = new Proxy(baseQueryKeys, {
  get(target, property) {
    if (typeof property !== 'string' || !(property in target)) {
      return undefined;
    }
    const value = target[property as keyof typeof target];
    if (typeof value !== 'function') return value;
    return (...args: unknown[]) => {
      const keyFactory = value as (...items: unknown[]) => readonly unknown[];
      const queryKey = keyFactory(...args);
      return scopeQueryKey(queryKey);
    };
  },
}) as typeof baseQueryKeys;

export type QueryKey = ReturnType<(typeof queryKeys)[keyof typeof queryKeys]>;

export const gridPaths = {
  organisations: 'organisations/query',
  projects: 'projects/query',
  users: 'users/query',
  features: 'features/query',
  releases: 'releases/query',
  releasesRelated: 'releases/related',
  coverageRuns: 'coverage-runs/query',
  uploads: 'uploads/query',
  coverageFiles: 'coverage-files/query',
  resultsFiles: 'results-files/query',
  executionRuns: 'execution-runs/query',
  suites: 'suites/query',
  tests: 'tests/query',
  suiteRuns: 'suite-runs/query',
  testRuns: 'test-runs/query',
  builds: 'pipeline-runs/query',
  jobRuns: 'job-runs/query',
  jobs: 'jobs/query',
  pipelines: 'pipelines/query',
} as const;

export type GridPath = (typeof gridPaths)[keyof typeof gridPaths];

export const gridQueryKey = (path: string): QueryKey => queryKeys.dataGrid(path);

export const invalidateKey = (key: QueryKey): QueryFilters => ({ queryKey: key });
export const invalidateGridQuery = (path: string): QueryFilters => ({
  queryKey: gridQueryKey(path),
});

const invalidateByKeyFactory =
  <TArgs extends unknown[]>(keyFn: (...args: TArgs) => QueryKey) =>
  (...args: TArgs): QueryFilters =>
    invalidateKey(keyFn(...args));

export const invalidateProjects = invalidateByKeyFactory(queryKeys.projects);
export const invalidateProjectDetail = invalidateByKeyFactory(queryKeys.projectDetail);
export const invalidateProjectById = invalidateByKeyFactory(queryKeys.projectById);
export const invalidateFeatures = invalidateByKeyFactory(queryKeys.features);
export const invalidateFeatureDetail = invalidateByKeyFactory(queryKeys.featureDetail);
export const invalidateReleases = invalidateByKeyFactory(queryKeys.releases);
export const invalidateReleaseDetail = invalidateByKeyFactory(queryKeys.releaseDetail);
export const invalidateReleaseDetails = invalidateByKeyFactory(queryKeys.releaseDetails);
export const invalidateSuites = invalidateByKeyFactory(queryKeys.suites);
export const invalidateSuiteDetail = invalidateByKeyFactory(queryKeys.suiteDetail);
export const invalidateSuiteRuns = invalidateByKeyFactory(queryKeys.suiteRuns);
export const invalidateSuiteRunSummaries = (): QueryFilters =>
  invalidateKey(queryKeys.suiteRunSummaryPrefix());
export const invalidateSuiteRunDetail = invalidateByKeyFactory(queryKeys.suiteRunDetail);
export const invalidateTests = invalidateByKeyFactory(queryKeys.tests);
export const invalidateTestDetail = invalidateByKeyFactory(queryKeys.testDetail);
export const invalidateExecutionRuns = invalidateByKeyFactory(queryKeys.executionRuns);
export const invalidateExecutionRunDetail = invalidateByKeyFactory(queryKeys.executionRunDetail);
export const invalidateExecutionRunSummaries = (): QueryFilters =>
  invalidateKey(queryKeys.executionRunSummaryPrefix());
export const invalidateBuilds = invalidateByKeyFactory(queryKeys.builds);
export const invalidateJobs = invalidateByKeyFactory(queryKeys.jobs);
export const invalidateBuildDetail = invalidateByKeyFactory(queryKeys.buildDetail);
export const invalidateBuildRunDetail = invalidateByKeyFactory(queryKeys.buildRunDetail);
export const invalidateJobDetail = invalidateByKeyFactory(queryKeys.jobDetail);
export const invalidatePipelines = invalidateByKeyFactory(queryKeys.pipelines);
export const invalidatePipelineSummaries = (): QueryFilters =>
  invalidateKey(queryKeys.pipelineSummaryPrefix());
export const invalidatePipelineRunSummaries = (): QueryFilters =>
  invalidateKey(queryKeys.pipelineRunSummaryPrefix());

export const invalidatePipelineBuildSummaries = invalidatePipelineRunSummaries;
export const invalidatePipelineDetail = invalidateByKeyFactory(queryKeys.pipelineDetail);
export const invalidatePipelineDetails = invalidateByKeyFactory(queryKeys.pipelineDetailPrefix);
export const invalidatePipelineStageDetail = invalidateByKeyFactory(queryKeys.pipelineStageDetail);
export const invalidatePipelineStageExecutionDetail = invalidateByKeyFactory(
  queryKeys.pipelineStageExecutionDetail,
);
export const invalidateMetrics = invalidateByKeyFactory(queryKeys.metrics);
export const invalidateMetricsBatchPrefix = invalidateByKeyFactory(queryKeys.metricsBatchPrefix);
export const invalidateMetricsPrefix = invalidateByKeyFactory(queryKeys.metricsPrefix);
export const invalidateUploads = invalidateByKeyFactory(queryKeys.uploads);
export const invalidateCoverageRuns = invalidateByKeyFactory(queryKeys.coverageRuns);
export const invalidateCoverageFiles = invalidateByKeyFactory(queryKeys.coverageFiles);
export const invalidateResultsFiles = invalidateByKeyFactory(queryKeys.resultsFiles);
export const invalidateResultsFileDetails = invalidateByKeyFactory(
  queryKeys.resultsFileDetailPrefix,
);
export const invalidateTestRuns = invalidateByKeyFactory(queryKeys.testRuns);
export const invalidateTestRunSummaries = (): QueryFilters =>
  invalidateKey(queryKeys.testRunSummaryPrefix());
export const invalidateTestRunDetail = invalidateByKeyFactory(queryKeys.testRunDetail);
export const invalidateUsers = invalidateByKeyFactory(queryKeys.users);
export const invalidateOrganisationInvites = invalidateByKeyFactory(queryKeys.organisationInvites);
export const invalidateSuperuserUsers = invalidateByKeyFactory(queryKeys.superuserUsers);
export const invalidateSuperuserAllowedSignups = invalidateByKeyFactory(
  queryKeys.superuserAllowedSignups,
);
export const invalidateSuperuserFeedback = invalidateByKeyFactory(queryKeys.superuserFeedback);
export const invalidateCurrentUserRoles = invalidateByKeyFactory(queryKeys.currentUserRolePrefix);
export const invalidateUserDetail = invalidateByKeyFactory(queryKeys.userDetail);

export const invalidateOrganisations = (userId?: string | null): QueryFilters =>
  invalidateKey(userId ? queryKeys.organisations(userId) : queryKeys.organisationsPrefix());

export const invalidateDataGridPathPrefix = (pathPrefix: string): QueryFilters => ({
  predicate: (query) => {
    const queryKey = query.queryKey;
    if (!Array.isArray(queryKey) || queryKey.length < 4) {
      return false;
    }
    if (queryKey[0] !== 'scope' || queryKey[2] !== 'datagrid') {
      return false;
    }
    const path = queryKey[3];
    return typeof path === 'string' && path.startsWith(pathPrefix);
  },
});
